/**
 * The analyst facts of a selected entity (PRD-0075 R-C3, R-C4): the view, its dialogs and the
 * marks they send. An entity without a stable id (`_uid`) takes no marks, and shows nothing.
 */
import { useMemo } from 'react'
import { entityUid } from '@/features/entity-history/history-rows'
import { useCaseSchema } from '@/hooks/use-case-queries'
import { AddFactDialog, type SourceDocumentOption } from './AddFactDialog'
import { EntityFactsView } from './EntityFactsView'
import { factKeyOptions, toAssertRequest, toInvalidateRequest } from './fact-forms'
import { entityFactsModel } from './fact-marks'
import { MarkDialog } from './MarkDialog'
import { useShowNoLongerValid } from './useFactVisibility'
import { useMarkDialogs } from './useMarkDialogs'

export interface EntityFactsProps {
  caseId: string
  /** The node's own properties; its `_uid` names the entity. */
  properties: Record<string, unknown>
  /** The entity's type, whose schema properties the "add fact" dialog offers. */
  typeLabel: string | null
  documents: readonly SourceDocumentOption[]
}

/** The keys a fact may name: the type's schema properties, then the others the entity holds. */
function useFactKeys(
  caseId: string,
  typeLabel: string | null,
  properties: Record<string, unknown>
) {
  const schema = useCaseSchema(caseId)
  return useMemo(() => {
    const type = schema.data?.entity_types?.find(entityType => entityType.label === typeLabel)
    return factKeyOptions(properties, type?.properties.map(property => property.name) ?? [])
  }, [schema.data, typeLabel, properties])
}

export function EntityFacts({ caseId, properties, typeLabel, documents }: EntityFactsProps) {
  const [show] = useShowNoLongerValid()
  const dialogs = useMarkDialogs<'addFact' | 'invalidateProperty' | 'retireEntity'>(caseId)
  const model = useMemo(() => entityFactsModel(properties), [properties])
  const keys = useFactKeys(caseId, typeLabel, properties)
  const uid = entityUid(properties)
  if (!uid) return null
  const submit = { pending: dialogs.pending, error: dialogs.error }
  return (
    <>
      <EntityFactsView
        model={model}
        showNoLongerValid={show}
        pending={dialogs.pending}
        actions={{
          onAddFact: () => dialogs.open('addFact'),
          onInvalidate: () => dialogs.open('invalidateProperty'),
          onRetire: () => dialogs.open('retireEntity'),
          onRevalidate: key =>
            dialogs.send({ kind: 'revalidateProperty', uid, key }, 'revalidated'),
          onRestore: () => dialogs.send({ kind: 'restoreEntity', uid }, 'restored'),
        }}
      />
      <AddFactDialog
        open={dialogs.dialog === 'addFact'}
        onOpenChange={open => !open && dialogs.close()}
        choices={{ keys, documents }}
        submit={{
          ...submit,
          onSubmit: values =>
            dialogs.send({ kind: 'assert', uid, body: toAssertRequest(values) }, 'asserted'),
        }}
      />
      <MarkDialog
        kind={dialogs.dialog === 'addFact' ? null : dialogs.dialog}
        onOpenChange={open => !open && dialogs.close()}
        keys={keys.filter(key => key in properties)}
        submit={{
          ...submit,
          onSubmit: values =>
            dialogs.dialog === 'retireEntity'
              ? dialogs.send(
                  { kind: 'retireEntity', uid, body: { reason: values.reason.trim() } },
                  'retired'
                )
              : dialogs.send(
                  {
                    kind: 'invalidateProperty',
                    uid,
                    key: values.key,
                    body: toInvalidateRequest(values),
                  },
                  'invalidated'
                ),
        }}
      />
    </>
  )
}
